import { CircleAlert } from "lucide-react"
import type * as React from "react"
import { type FieldValues, FormProvider, type SubmitHandler, type UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { isApiError } from "@/api/client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Spinner } from "@/components/ui/spinner"
import { applyApiErrorToForm } from "@/lib/form"
import { cn } from "@/lib/utils"

/**
 * Wrap a submit handler so API errors land in the form: 422 `fields` become
 * inline errors; other ApiErrors are left to the mutation toast; unexpected
 * errors become a root error.
 */
export function useFormSubmit<T extends FieldValues>(form: UseFormReturn<T>, onSubmit: SubmitHandler<T>) {
  return form.handleSubmit(async (values, event) => {
    form.clearErrors("root")
    try {
      await onSubmit(values, event)
    } catch (err) {
      if (!isApiError(err) || err.fields) applyApiErrorToForm(err, form)
    }
  })
}

export interface FormRootProps<T extends FieldValues>
  extends Omit<React.ComponentProps<"form">, "onSubmit" | "children"> {
  form: UseFormReturn<T>
  onSubmit: SubmitHandler<T>
  children: React.ReactNode
}

/** `<form>` + react-hook-form context + API error mapping. */
export function FormRoot<T extends FieldValues>({ form, onSubmit, children, ...props }: FormRootProps<T>) {
  const submit = useFormSubmit(form, onSubmit)
  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={submit} {...props}>
        {children}
      </form>
    </FormProvider>
  )
}

/** Renders `errors.root.server` (non-field API errors) as an inline alert. */
export function FormRootError<T extends FieldValues>({
  form,
  className,
}: {
  form: UseFormReturn<T>
  className?: string
}) {
  const message = form.formState.errors.root?.server?.message
  if (!message) return null
  return (
    <Alert variant="destructive" className={cn("border-destructive/30", className)}>
      <CircleAlert />
      <AlertDescription className="whitespace-pre-line">{message}</AlertDescription>
    </Alert>
  )
}

export function SubmitButton<T extends FieldValues>({
  form,
  children,
  disabledWhenPristine = false,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "form"> & {
  form: UseFormReturn<T>
  disabledWhenPristine?: boolean
}) {
  const { isSubmitting, isDirty } = form.formState
  return (
    <Button
      type="submit"
      disabled={isSubmitting || (disabledWhenPristine && !isDirty) || props.disabled}
      {...props}
    >
      {isSubmitting && <Spinner />}
      {children}
    </Button>
  )
}

interface OverlayFormProps<T extends FieldValues> {
  open: boolean
  onOpenChange: (open: boolean) => void
  form: UseFormReturn<T>
  onSubmit: SubmitHandler<T>
  title: React.ReactNode
  description?: React.ReactNode
  submitLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
  /** Extra footer content on the left (e.g. a destructive "Remove" button). */
  footerStart?: React.ReactNode
  /** Disable submit until the user changed something (edit forms). */
  requireDirty?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * Modal form. Typical use:
 * ```tsx
 * <FormDialog open={open} onOpenChange={setOpen} form={form} title="New organization"
 *   onSubmit={async (v) => { await create.mutateAsync(v); setOpen(false) }}>
 *   <TextField control={form.control} name="name" label="Name" />
 * </FormDialog>
 * ```
 */
export function FormDialog<T extends FieldValues>({
  open,
  onOpenChange,
  form,
  onSubmit,
  title,
  description,
  submitLabel,
  cancelLabel,
  footerStart,
  requireDirty,
  className,
  children,
}: OverlayFormProps<T>) {
  const { t } = useTranslation()
  const submitting = form.formState.isSubmitting
  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className={cn("sm:max-w-lg", className)}>
        <FormRoot form={form} onSubmit={onSubmit} className="grid gap-5">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <div className="grid gap-4">{children}</div>
          <FormRootError form={form} />
          <DialogFooter className={cn(footerStart && "sm:justify-between")}>
            {footerStart && <div className="flex gap-2">{footerStart}</div>}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {cancelLabel ?? t("common.cancel")}
              </Button>
              <SubmitButton form={form} disabledWhenPristine={requireDirty}>
                {submitLabel ?? t("common.save")}
              </SubmitButton>
            </div>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  )
}

/** Side-sheet form, for longer create/edit flows (members, agents…). Same API as {@link FormDialog}. */
export function FormSheet<T extends FieldValues>({
  open,
  onOpenChange,
  form,
  onSubmit,
  title,
  description,
  submitLabel,
  cancelLabel,
  footerStart,
  requireDirty,
  className,
  children,
}: OverlayFormProps<T>) {
  const { t } = useTranslation()
  const submitting = form.formState.isSubmitting
  return (
    <Sheet open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <SheetContent className={cn("gap-0 sm:max-w-lg", className)}>
        <FormRoot form={form} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <SheetHeader className="border-b px-5 py-4">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            <div className="grid gap-5">{children}</div>
            <FormRootError form={form} className="mt-5" />
          </div>
          <SheetFooter className="bg-muted/30 flex-row items-center justify-between border-t px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex gap-2">{footerStart}</div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {cancelLabel ?? t("common.cancel")}
              </Button>
              <SubmitButton form={form} disabledWhenPristine={requireDirty}>
                {submitLabel ?? t("common.save")}
              </SubmitButton>
            </div>
          </SheetFooter>
        </FormRoot>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Sticky "unsaved changes" bar for long page forms (settings). Shows when the
 * form is dirty, with Discard / Save.
 */
export function UnsavedChangesBar<T extends FieldValues>({
  form,
  onDiscard,
}: {
  form: UseFormReturn<T>
  onDiscard: () => void
}) {
  const { t } = useTranslation()
  const { isDirty, isSubmitting } = form.formState
  return (
    <div
      aria-hidden={!isDirty}
      className={cn(
        "pointer-events-none sticky bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 mt-8 flex justify-center transition-all duration-200 max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom))]",
        isDirty ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      )}
    >
      <div className="bg-popover/95 supports-backdrop-filter:bg-popover/80 pointer-events-auto flex w-full max-w-xl items-center justify-between gap-3 rounded-xl border px-4 py-2.5 shadow-lg backdrop-blur">
        <span className="text-sm font-medium">{t("form.unsaved")}</span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={isSubmitting || !isDirty}
          >
            {t("form.discard")}
          </Button>
          <SubmitButton form={form} size="sm" disabled={!isDirty}>
            {t("form.saveChanges")}
          </SubmitButton>
        </div>
      </div>
    </div>
  )
}
